import { type IntlShape, createIntl, createIntlCache } from 'react-intl-next';
import { ref, proxy } from 'valtio';

import { fetchLocaleMessages } from './fetch-locale-messages';
import { externalMessages, i18n } from './i18n-state';
import { type ExternalMessageLoaders, type Product } from './intl-types';

export function getExternalMessagesPromiseFromLoaders(
  externalMessageLoaders: ExternalMessageLoaders
) {
  return Object.entries(externalMessageLoaders).map(async ([key, loadFns]) => {
    const results = await Promise.all(loadFns.map((loadFn) => loadFn()));
    const messages = results.reduce((acc, result) => ({ ...acc, ...result.default }), {});
    return { key, messages };
  });
}

// Silence any formatJS errors for local dev
const onError = () => {};

/**
 * A wrapper around fetchLocaleMessages that sets the intl object on the
 * valtio i18n state and loads external messages, eg those from atlaskit dependencies
 */
export async function createIntlObject(
  product: Product,
  externalMessageLoaders: ExternalMessageLoaders,
  locale: string
) {
  i18n.status = 'fetching';
  const productMessagesPromise = fetchLocaleMessages(product, locale);

  const externalMessagePromises =
    getExternalMessagesPromiseFromLoaders(externalMessageLoaders);

  const [messages, ...external] = await Promise.all([
    productMessagesPromise,
    ...externalMessagePromises,
  ]);

  i18n.intl = ref(
    createIntl(
      {
        locale,
        messages,
        onError,
      },
      /**
       * {@see https://formatjs.io/docs/react-intl/api/#createintlcache}
       */
      createIntlCache()
    )
  );

  // Each external message is a proxy object that you can subscribe to any changes for, ie to
  // re-render once the messages are ready
  for (const { key, messages } of external) {
    if (!externalMessages[key]) {
      // The intl object was not yet initialised, so we want to do this now. We should immediately
      // overwrite this .intl property
      externalMessages[key] = proxy<{ intl: IntlShape }>({ intl: {} as IntlShape });
    }

    // Overwrite the intl object in the proxy to induce a re-render if this object already existed
    externalMessages[key].intl = ref(
      createIntl(
        {
          locale,
          messages,
          onError,
        },
        createIntlCache()
      )
    );
  }
  i18n.status = 'succeeded';
}
